import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Alert, Spin, Button, Space, Switch, Progress, Empty, Typography, notification } from 'antd';
import {
  UserOutlined,
  ShoppingOutlined,
  WarningOutlined,
  UserAddOutlined,
  ReloadOutlined,
  PlusOutlined,
  WalletOutlined,
  GiftOutlined,
  CodeOutlined,
  FileProtectOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { formatMoney } from '../../utils/formatMoney';
import { formatDateTime, formatDate } from '../../utils/dateFormat';
import { orderStatusLabel, sourceLabel } from '../../constants/orderDisplay';
import {
  PageHeader,
  StatCard,
  StatGrid,
  StatusTag,
  TableCard,
} from '../../components/admin';

const { Text } = Typography;

const ORDER_TONE = { pending: 'warning', completed: 'success', failed: 'danger' };
const CLIENT_TONE = { pending: 'warning', approved: 'success', rejected: 'danger', suspended: 'default' };

const capitalize = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '-');

/** One clickable row in the recent-activity lists. */
const ActivityRow = ({ title, tag, meta, time, onClick }) => (
  <div
    className="dash-row"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter') onClick();
    }}
  >
    <div className="dash-row__main">
      <div className="dash-row__title">
        <span className="cell-main">{title}</span>
        {tag}
      </div>
      <div className="cell-sub">{meta}</div>
    </div>
    <div className="dash-row__time">{time}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    statistics: {},
    recentActivities: { orders: [], clients: [] },
    orderStatusStats: [],
    stockAlerts: [],
  });

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await axios.get('/admin/dashboard');

      // The render path destructures statistics/recentActivities/etc; if
      // the backend ever returns a partial payload (or an error envelope
      // without `data`) the dashboard would white-screen. Merge into a
      // stable shape so optional sub-fields stay safely accessible.
      const payload = response?.data || {};
      setDashboardData({
        statistics: payload.statistics || {},
        recentActivities: {
          orders: payload.recentActivities?.orders || [],
          clients: payload.recentActivities?.clients || [],
        },
        orderStatusStats: payload.orderStatusStats || [],
        stockAlerts: payload.stockAlerts || [],
        ...payload,
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.message);
      if (isRefresh) {
        notification.error({
          message: 'Refresh failed',
          description: 'The dashboard could not be refreshed. Showing the last loaded figures.',
          duration: 3,
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto refresh every 30 seconds while switched on.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => fetchDashboardData(true), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchDashboardData]);

  const go = (route, state = {}) => navigate(route, { state });

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !lastUpdated) {
    return (
      <Alert
        message="The dashboard could not be loaded"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => fetchDashboardData()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { statistics, recentActivities, stockAlerts } = dashboardData;
  const attention = statistics.attention || {};

  // Open the orders page on the first queue that actually has something in it.
  const openAttentionQueue = () => {
    const queue =
      attention.uncertain > 0
        ? 'uncertain'
        : attention.refundFailed > 0
          ? 'refund_failed'
          : attention.reconciling > 0
            ? 'reconciling'
            : attention.stuckPending > 0
              ? 'stuck_pending'
              : null;
    go('/admin/orders', queue ? { attention: queue } : {});
  };

  const attentionParts = [
    attention.uncertain ? `${attention.uncertain} outcome unknown` : null,
    attention.refundFailed ? `${attention.refundFailed} refund failed` : null,
    attention.reconciling ? `${attention.reconciling} reconciling` : null,
    attention.stuckPending ? `${attention.stuckPending} pending > 30 min` : null,
  ].filter(Boolean);

  const pendingTopUps = statistics.pendingTopUpRequests || 0;
  const ordersNeedingAttention = statistics.ordersNeedingAttention || 0;
  const lowStock = statistics.lowStockCount || 0;
  const pendingClients = statistics.pendingClients || 0;
  const queueTotal = pendingTopUps + ordersNeedingAttention + lowStock + pendingClients;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={
          queueTotal > 0
            ? `${queueTotal} item${queueTotal === 1 ? '' : 's'} waiting for the operations team.`
            : 'All queues are clear.'
        }
        extra={
          <>
            <Space size={8} style={{ marginRight: 4 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
              </Text>
              <Switch size="small" checked={autoRefresh} onChange={setAutoRefresh} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Auto refresh
              </Text>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchDashboardData(true)} loading={refreshing}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => go('/admin/vouchers', { openCreateModal: true })}
            >
              Add voucher
            </Button>
          </>
        }
      />

      <h2 className="group-heading">Needs action</h2>
      <StatGrid min={210}>
        <StatCard
          label="Top-ups to review"
          value={pendingTopUps}
          icon={<WalletOutlined />}
          tone={pendingTopUps > 0 ? 'warning' : 'default'}
          highlight={pendingTopUps > 0}
          hint={pendingTopUps > 0 ? 'Check proof, then credit the wallet' : 'Nothing waiting'}
          onClick={() => go('/admin/topup-requests')}
        />
        <StatCard
          label="Orders needing a person"
          value={ordersNeedingAttention}
          icon={<FileProtectOutlined />}
          tone={ordersNeedingAttention > 0 ? 'danger' : 'default'}
          highlight={ordersNeedingAttention > 0}
          hint={attentionParts.length ? attentionParts.join(' · ') : 'Nothing waiting'}
          onClick={openAttentionQueue}
        />
        <StatCard
          label="Client registrations"
          value={pendingClients}
          icon={<UserAddOutlined />}
          tone={pendingClients > 0 ? 'warning' : 'default'}
          highlight={pendingClients > 0}
          hint={pendingClients > 0 ? 'Waiting for approval' : 'Nothing waiting'}
          onClick={() => go('/admin/clients', { filterStatus: 'pending' })}
        />
        <StatCard
          label="Low stock vouchers"
          value={lowStock}
          icon={<WarningOutlined />}
          tone={lowStock > 0 ? 'warning' : 'default'}
          highlight={lowStock > 0}
          hint={lowStock > 0 ? 'Under 20% of codes left' : 'Stock levels fine'}
          onClick={() => go('/admin/vouchers', { filterLowStock: true })}
        />
      </StatGrid>

      <h2 className="group-heading">Platform</h2>
      <StatGrid min={210}>
        <StatCard
          label="Clients"
          value={(statistics.totalClients || 0).toLocaleString()}
          icon={<UserOutlined />}
          tone="primary"
          onClick={() => go('/admin/clients')}
        />
        <StatCard
          label="Active vouchers"
          value={(statistics.activeVouchers || 0).toLocaleString()}
          icon={<GiftOutlined />}
          tone="primary"
          onClick={() => go('/admin/vouchers')}
        />
        <StatCard
          label="Orders"
          value={(statistics.totalOrders || 0).toLocaleString()}
          icon={<ShoppingOutlined />}
          tone="primary"
          onClick={() => go('/admin/orders')}
        />
        <StatCard
          label="Available inventory"
          value={(statistics.totalAvailableInventory ?? statistics.totalVoucherCodes ?? 0).toLocaleString()}
          icon={<CodeOutlined />}
          tone="success"
          hint="Codes across active vouchers"
        />
      </StatGrid>

      {stockAlerts && stockAlerts.length > 0 ? (
        <TableCard
          title="Low stock"
          count={stockAlerts.length}
          extra={
            <Button type="link" size="small" onClick={() => go('/admin/vouchers', { filterLowStock: true })}>
              Open in vouchers <RightOutlined />
            </Button>
          }
          className="dash-block"
        >
          <div className="dash-list">
            {stockAlerts.map((item) => {
              const pct = Math.round((item.stockPercentage || 0) * 100);
              return (
                <div className="dash-stock" key={item._id || `${item.name}-${item.brand}`}>
                  <div className="dash-stock__name">
                    <span className="cell-main">{item.name}</span>
                    <span className="cell-sub">{item.brand}</span>
                  </div>
                  <Progress
                    percent={pct}
                    size="small"
                    showInfo={false}
                    status={item.stockPercentage < 0.1 ? 'exception' : 'normal'}
                    strokeColor={item.stockPercentage < 0.1 ? undefined : 'var(--c-warning)'}
                    style={{ margin: 0, flex: '1 1 160px', minWidth: 120 }}
                  />
                  <span className="dash-stock__count">
                    {item.availableQuantity} / {item.totalQuantity} left
                  </span>
                </div>
              );
            })}
          </div>
        </TableCard>
      ) : null}

      <Row gutter={[16, 16]} className="dash-block">
        <Col xs={24} xl={12}>
          <TableCard
            title="Recent orders"
            extra={
              <Button type="link" size="small" onClick={() => go('/admin/orders')}>
                View all <RightOutlined />
              </Button>
            }
          >
            <div className="dash-list">
              {recentActivities.orders.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No orders yet" />
              ) : (
                recentActivities.orders.map((order) => (
                  <ActivityRow
                    key={order._id}
                    onClick={() => go('/admin/orders', { orderId: order._id })}
                    title={<span className="cell-mono">{order.orderNumber}</span>}
                    tag={
                      <StatusTag tone={ORDER_TONE[order.status] || 'default'}>
                        {orderStatusLabel(order.status)}
                      </StatusTag>
                    }
                    meta={[
                      order.clientId?.companyName,
                      formatMoney(order.totalAmount, order.currency),
                      order.itemsSummary?.[0]?.name,
                      order.source ? sourceLabel(order.source) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    time={formatDateTime(order.createdAt)}
                  />
                ))
              )}
            </div>
          </TableCard>
        </Col>
        <Col xs={24} xl={12}>
          <TableCard
            title="Recent clients"
            extra={
              <Button type="link" size="small" onClick={() => go('/admin/clients')}>
                View all <RightOutlined />
              </Button>
            }
          >
            <div className="dash-list">
              {recentActivities.clients.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No clients yet" />
              ) : (
                recentActivities.clients.map((client) => (
                  <ActivityRow
                    key={client._id}
                    onClick={() => go('/admin/clients', { clientId: client._id })}
                    title={client.companyName}
                    tag={
                      <StatusTag tone={CLIENT_TONE[client.status] || 'default'}>
                        {capitalize(client.status)}
                      </StatusTag>
                    }
                    meta={[client.email, client.tier ? `${client.tier} tier` : null].filter(Boolean).join(' · ')}
                    time={formatDate(client.createdAt)}
                  />
                ))
              )}
            </div>
          </TableCard>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;
